import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Axios from 'axios';
import AppLayout from '../../components/AppLayout';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { S, Icon, StatusBadge, formatDate, formatMoney } from '../regureStyles';

// Portal-scoped policy detail. Uses GET /me/my-policies/:id which enforces
// portalLink (broker.value | policyholder.email) so the response is already
// filtered to policies this user is allowed to see. Rendering stays read-
// only — portal users must never see the back-office PolicyDetailScreen.
const MyPolicyDetailScreen = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams();
  const userLogin = useSelector(state => state.userLogin);
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (userLogin?.userInfo?.status !== 'success') return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await Axios.get(`${apiUrl}/me/my-policies/${id}`, { withCredentials: true });
        if (!cancelled) setPolicy(response.data?.data || null);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || t('screens.portal.failedLoadPolicies'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userLogin, apiUrl, id, t]);

  if (loading) return (
    <AppLayout>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularLoader />
      </div>
    </AppLayout>
  );

  // Tenant-configured date order and currency, not the browser's and not
  // a hardcoded CHF — the portal rendered Swiss francs for every tenant.
  const fmtDate = d => formatDate(d);
  const fmtMoney = (n, ccy) => n != null ? formatMoney(n, ccy) : '—';
  const productLabel = policy?.product || (policy?.lineOfBusiness ? policy.lineOfBusiness.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '—');

  const Field = ({ label, value }) => (
    <div style={{ padding: '12px 0', borderBottom: `1px solid ${S.borderLight}` }}>
      <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: S.textPrimary, fontWeight: 500 }}>{value || '—'}</div>
    </div>
  );

  return (
    <AppLayout>
      <div style={{
        flex: 1,
        overflowY: 'auto',
        background: `linear-gradient(135deg, ${S.bg} 0%, #f8fafc 100%)`,
        padding: '32px 40px',
        minHeight: '100%',
      }}>
        <button
          onClick={() => history.push('/portal/my-policies')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: S.textMuted, fontSize: 13, fontWeight: 600, padding: 0, marginBottom: 16,
          }}
        >
          <Icon name="chevron-left" size={14} />
          {t('screens.portal.breadcrumbMyPolicies')}
        </button>

        {error && (
          <div style={{
            background: 'rgba(220, 38, 38, 0.08)',
            color: '#dc2626',
            padding: '14px 18px',
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            border: '1px solid rgba(220, 38, 38, 0.15)',
          }}>
            {error}
          </div>
        )}

        {policy && (
          <>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {productLabel}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: S.textPrimary, letterSpacing: '-0.5px' }}>
                  {policy.policyNumber || '—'}
                </div>
                <StatusBadge status={policy.status || 'active'} />
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 20,
            }}>
              <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '8px 24px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, padding: '16px 0 8px' }}>{t('screens.portal.detailPolicyholder', 'Policyholder')}</div>
                <Field label={t('screens.portal.policiesColPolicyNumber')} value={policy.policyNumber} />
                <Field label={t('screens.portal.detailPolicyholderName', 'Name')} value={policy.policyholder?.name} />
                <Field label={t('screens.portal.detailCompany', 'Company')} value={policy.policyholder?.company} />
                <Field label={t('screens.portal.detailEmail', 'Email')} value={policy.policyholder?.email} />
              </div>

              <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '8px 24px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, padding: '16px 0 8px' }}>{t('screens.portal.detailCoverage', 'Coverage')}</div>
                <Field label={t('screens.portal.policiesColProduct')} value={productLabel} />
                <Field label={t('screens.portal.policiesColPremium')} value={fmtMoney(policy.annualPremium, policy.currency)} />
                <Field label={t('screens.portal.detailStart', 'Start date')} value={fmtDate(policy.startDate)} />
                <Field label={t('screens.portal.detailEnd', 'End date')} value={fmtDate(policy.endDate)} />
              </div>

              <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 14, padding: '8px 24px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: S.textPrimary, padding: '16px 0 8px' }}>{t('screens.portal.detailBroker', 'Broker & Carrier')}</div>
                <Field label={t('screens.portal.policiesColBroker')} value={policy.brokerName} />
                <Field label={t('screens.portal.detailCarrier', 'Risk carrier')} value={policy.riskCarrierName || policy.carrierName} />
                <Field label={t('screens.portal.detailIssued', 'Issued')} value={fmtDate(policy.createdAt)} />
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
};

export default MyPolicyDetailScreen;
